import type { ReactNode } from 'react';
import { MultiSelect, type SelectOption } from '@nyce/admin-kit/ui';
import { matchesScope } from '../../../identity/api';
import { CLAIM_CATALOG } from '../../../identity/claimCatalog';

const isWildcard = (s: string) => s.endsWith(':*');

/** Every known scope a wildcard covers (catalog + `extra`, e.g. other roles' scopes), without the wildcard itself. */
export function narrowerScopes(wildcard: string, extra: string[] = []): string[] {
  const known = [
    ...CLAIM_CATALOG.flatMap((g) => g.claims.filter((c) => c.type === 'scope').map((c) => c.value)),
    ...extra,
  ];
  return [...new Set(known)].filter((s) => s !== wildcard && matchesScope([wildcard], s)).sort();
}

/** The tenant's scope claims, then the known narrower scopes under each wildcard among them (grouped per wildcard). */
export function scopeOptions(ceiling: string[], known: string[] = []): SelectOption[] {
  const seen = new Set(ceiling);
  const options: SelectOption[] = ceiling.map((s) => ({ value: s, label: s, group: 'Tenant scopes' }));
  for (const w of ceiling.filter(isWildcard))
    for (const s of narrowerScopes(w, known)) {
      if (seen.has(s)) continue;
      seen.add(s);
      options.push({ value: s, label: s, group: `Under ${w}` });
    }
  return options;
}

/**
 * Scopes of a role or service client (`docs/ui-kit.md` multi-select): the tenant's scope claims and the known scopes
 * under its wildcards to pick from, any other scope typed (`buddy:ask`).
 */
export function ScopeSelect({
  ceiling,
  value,
  onChange,
  known = [],
  hint,
  placeholder,
}: {
  ceiling: string[];
  value: string[];
  onChange: (v: string[]) => void;
  /** Scopes seen elsewhere on the tenant, offered under a matching wildcard. */
  known?: string[];
  hint?: ReactNode;
  placeholder?: string;
}) {
  return (
    <MultiSelect
      label="Scopes"
      fieldClassName="nyk-span2"
      data-testid="scope-select"
      options={scopeOptions(ceiling, known)}
      value={value}
      onChange={onChange}
      creatable
      hint={hint}
      placeholder={placeholder}
    />
  );
}
